import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Ear, SignOut, Users, Package, MagnifyingGlass, Pencil, Trash, Plus, Percent, ChartLine, Tag, DownloadSimple, FileCsv, EnvelopeSimple, ShoppingCart, Timer, HourglassHigh, CheckCircle, Buildings, Wrench, Gift, X, Key, Stamp, ShieldCheck } from '@phosphor-icons/react';
import { AdminStats } from './StatsCharts';
import ShopifyTab from './ShopifyTab';
import ClientAccountManager from './ClientAccountManager';
import AdminSavModule from './AdminSavModule';
import SavStats from './SavStats';
import MdrSettingsPanel from './MdrSettingsPanel';
import TechValueStats from './TechValueStats';
import { getOrderBusinessHours, formatBusinessHours, getUrgencyLevel } from '@/utils/businessHours';
import AuthImage from '@/components/AuthImage';
import {
  AddUserDialog, EditUserDialog, ClientDetailDialog, DiscountDialog,
  ProductDialog, PriceBreakdownDialog, DeleteOrderDialog, DeleteProductDialog,
  PromoInviteDialog, EstablishmentEditDialog
} from './AdminDialogs';
import MonthlyRecapDialog from './MonthlyRecapDialog';

function AdminCounterBadge({ hours, level, hasStl, threshold = 72 }) {
  if (level === 'none') {
    return <span className="text-xs text-zinc-400" data-testid="counter-badge">—</span>;
  }
  const display = formatBusinessHours(hours);
  const pct = Math.min(100, (hours / threshold) * 100);
  const dayLabel = threshold === 120 ? '5j' : '3j';
  const config = {
    ok: { bg: 'bg-emerald-100', text: 'text-emerald-800', bar: 'bg-emerald-500', icon: <Timer size={13} weight="bold" /> },
    warning: { bg: 'bg-yellow-100', text: 'text-yellow-800', bar: 'bg-yellow-500', icon: <HourglassHigh size={13} weight="bold" /> },
    critical: { bg: 'bg-orange-100', text: 'text-orange-800', bar: 'bg-orange-500', icon: <HourglassHigh size={13} weight="bold" /> },
    overdue: { bg: 'bg-red-100', text: 'text-red-800', bar: 'bg-red-600', icon: <HourglassHigh size={13} weight="bold" /> },
    finished: { bg: 'bg-zinc-100', text: 'text-zinc-600', bar: 'bg-zinc-400', icon: <CheckCircle size={13} weight="bold" /> },
  };
  const c = config[level] || config.ok;
  return (
    <div className={`inline-flex flex-col gap-0.5 ${c.bg} rounded px-2 py-1 min-w-[60px]`} data-testid="counter-badge">
      <div className={`flex items-center gap-1 ${c.text} text-xs font-bold`}>
        {c.icon}
        <span>{display}</span>
        <span className={`text-[9px] font-semibold opacity-70 ml-0.5 ${threshold === 120 ? 'text-blue-700' : ''}`} data-testid="counter-day-label">{dayLabel}</span>
        {level === 'finished' && <CheckCircle size={11} className="text-zinc-500" />}
        {!hasStl && level !== 'finished' && <span className="text-[9px] opacity-60 ml-0.5">STL?</span>}
      </div>
      <div className="w-full h-1 bg-white/60 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${c.bar} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AdminPanel({ tab = 'dashboard' }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || tab);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t) setActiveTab(t);
  }, [searchParams]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userPagination, setUserPagination] = useState({ page: 1, per_page: 50, total: 0, total_pages: 1 });
  const [orderPage, setOrderPage] = useState(1);
  const [orderPagination, setOrderPagination] = useState({ page: 1, per_page: 50, total: 0, total_pages: 1 });

  // Edit user dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Add user dialog
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    name: '', email: '', password: '', role: 'client',
    company_name: '', company_id: '', address_number: '', address_street: '', address_zip: '', address_city: '', phone: '',
    can_view_prices: false, can_view_commercial_terms: false, establishment_ids: []
  });

  // Companies & Establishments
  const [companies, setCompanies] = useState([]);
  const [allEstablishments, setAllEstablishments] = useState([]);
  const [showNewEstablishment, setShowNewEstablishment] = useState(false);
  const [newEstablishment, setNewEstablishment] = useState({ name: '', address: '', email: '', phone: '' });

  // Discount dialog (per product)
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountForm, setDiscountForm] = useState({});
  const [discountProducts, setDiscountProducts] = useState([]);

  // Client detail dialog
  const [clientDetailOpen, setClientDetailOpen] = useState(false);
  const [clientDetailUser, setClientDetailUser] = useState(null);
  const [clientOrderCount, setClientOrderCount] = useState(0);
  const [clientEstablishments, setClientEstablishments] = useState([]);
  const [companyEstablishments, setCompanyEstablishments] = useState([]);

  // Establishment edit dialog (créer/éditer une adresse d'établissement)
  const [estEditOpen, setEstEditOpen] = useState(false);
  const [estEditTarget, setEstEditTarget] = useState(null); // null = création, sinon = édition

  // Product dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', category: 'embout', base_price: 0
  });
  const [productFilter, setProductFilter] = useState('active'); // 'active' | 'archived' | 'all'
  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // CSV exports
  const [csvExports, setCsvExports] = useState([]);
  // Monthly recap PDF dialog
  const [recapOpen, setRecapOpen] = useState(false);

  // Admin stats
  const [adminStats, setAdminStats] = useState(null);

  // Order price breakdown dialog
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceOrder, setPriceOrder] = useState(null);

  // Order deletion
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  // Promo invite
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [promoUser, setPromoUser] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  // Marquage par défaut (utilisateur OU société — hérité par tous les users de la société)
  const [defaultMarquageDialogOpen, setDefaultMarquageDialogOpen] = useState(false);
  const [defaultMarquageUser, setDefaultMarquageUser] = useState(null);
  // target.type = 'user' (default, rétrocompat) ou 'company' — définit les URL d'API
  const [defaultMarquageTarget, setDefaultMarquageTarget] = useState(null);
  const [defaultMarquagePreview, setDefaultMarquagePreview] = useState('');
  const [defaultMarquageUploading, setDefaultMarquageUploading] = useState(false);

  // Helper : construit les URLs de marquage selon le type de cible.
  const marquageEndpoints = (target) => {
    if (!target) return null;
    const base = target.type === 'company'
      ? `${API}/admin/companies/${target.id}/default-marquage`
      : `${API}/admin/users/${target.id}/default-marquage`;
    return { get: base, post: base, delete: base };
  };

  const openDefaultMarquageDialog = async (u) => {
    // Rétrocompat : signature (user) → target de type 'user'.
    const target = {
      type: 'user',
      id: u.id,
      name: u.name,
      has_logo: !!u.default_marquage_image_id,
      current_scale: (typeof u.marquage_logo_scale === 'number') ? u.marquage_logo_scale : 1.5,
    };
    setDefaultMarquageUser(u);
    setDefaultMarquageTarget(target);
    setDefaultMarquageDialogOpen(true);
    if (target.has_logo) {
      try {
        const res = await axios.get(marquageEndpoints(target).get, { responseType: 'blob' });
        setDefaultMarquagePreview(URL.createObjectURL(res.data));
      } catch {
        setDefaultMarquagePreview('');
      }
    } else {
      setDefaultMarquagePreview('');
    }
  };

  // Nouveau : ouvre le dialog pour une société (logo hérité par tous ses users).
  const openCompanyMarquageDialog = async (company) => {
    const target = {
      type: 'company',
      id: company.id,
      name: company.name,
      has_logo: !!company.default_marquage_image_id,
      current_scale: (typeof company.marquage_logo_scale === 'number') ? company.marquage_logo_scale : 1.5,
    };
    setDefaultMarquageUser(null);
    setDefaultMarquageTarget(target);
    setDefaultMarquageDialogOpen(true);
    if (target.has_logo) {
      try {
        const res = await axios.get(marquageEndpoints(target).get, { responseType: 'blob' });
        setDefaultMarquagePreview(URL.createObjectURL(res.data));
      } catch {
        setDefaultMarquagePreview('');
      }
    } else {
      setDefaultMarquagePreview('');
    }
  };

  const handleDefaultMarquageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !defaultMarquageTarget) return;
    if (!file.name.toLowerCase().endsWith('.png')) { toast.error('Seuls les fichiers PNG sont acceptés'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 5 Mo)'); return; }
    setDefaultMarquageUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const eps = marquageEndpoints(defaultMarquageTarget);
      await axios.post(eps.post, fd);
      const res = await axios.get(eps.get, { responseType: 'blob' });
      setDefaultMarquagePreview(URL.createObjectURL(res.data));
      setDefaultMarquageTarget((prev) => prev ? { ...prev, has_logo: true } : prev);
      if (defaultMarquageTarget.type === 'user') {
        await fetchUsers();
        setDefaultMarquageUser((prev) => prev ? { ...prev, default_marquage_image_id: 'set' } : prev);
      } else {
        await fetchCompanies();
      }
      toast.success(defaultMarquageTarget.type === 'company' ? 'Marquage société enregistré' : 'Marquage par défaut enregistré');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload logo');
    } finally {
      setDefaultMarquageUploading(false);
    }
  };

  const handleDefaultMarquageDelete = async () => {
    if (!defaultMarquageTarget) return;
    try {
      await axios.delete(marquageEndpoints(defaultMarquageTarget).delete);
      setDefaultMarquagePreview('');
      setDefaultMarquageTarget((prev) => prev ? { ...prev, has_logo: false } : prev);
      if (defaultMarquageTarget.type === 'user') {
        setDefaultMarquageUser((prev) => prev ? { ...prev, default_marquage_image_id: null } : prev);
        await fetchUsers();
      } else {
        await fetchCompanies();
      }
      toast.success('Marquage supprimé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  const openPromoInvite = (u, e) => {
    if (e) e.stopPropagation();
    setPromoUser(u);
    setPromoDialogOpen(true);
  };

  const handlePromoBonus = async (u, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Offrir 5 embouts supplémentaires à ${u.name} ? (cumulable, validité 30 jours)`)) return;
    try {
      const res = await axios.post(`${API}/admin/users/${u.id}/promo-bonus`);
      toast.success(res.data.message || `5 embouts offerts à ${u.name}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'attribution");
    }
  };

  const handlePromoInvite = async () => {
    if (!promoUser) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/admin/users/${promoUser.id}/promo-invite`);
      if (res.data.warning) {
        toast.warning(res.data.warning);
      } else {
        toast.success(res.data.message || `Offre promotionnelle envoyée à ${promoUser.name}`);
      }
      setPromoDialogOpen(false);
      setPromoUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de l'offre");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;
    try {
      await axios.delete(`${API}/orders/${orderToDelete.id}`);
      toast.success('Commande supprimée');
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      fetchOrders();
      fetchAdminStats();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur suppression'); }
  };

  // Handle Shopify OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('shopify') === 'connected') {
      toast.success('Shopify connecté avec succès !');
      setActiveTab('shopify');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('shopify') === 'error') {
      toast.error("Erreur de connexion Shopify");
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    if (activeTab === 'users') { fetchUsers(); fetchAllProducts(); fetchCompanies(); fetchEstablishments(); }
    else if (activeTab === 'orders') fetchOrders();
    else if (activeTab === 'products') fetchProducts();
    else if (activeTab === 'exports') fetchCsvExports();
    else if (activeTab === 'shopify') { setLoading(false); }
    else if (activeTab === 'client-accounts') { setLoading(false); }
    else { fetchUsers(); fetchOrders(); fetchAdminStats(); }
  }, [activeTab, productFilter, debouncedSearch]);

  // Debounce searchQuery -> debouncedSearch (300ms) + reset to page 1 on search change
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (userPage !== 1) setUserPage(1);
      if (orderPage !== 1) setOrderPage(1);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch { /* silent */ }
  };

  const fetchEstablishments = async () => {
    try {
      const response = await axios.get(`${API}/establishments`);
      setAllEstablishments(response.data);
    } catch { /* silent */ }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', userPage);
      params.append('per_page', 50);
      if (debouncedSearch.trim()) params.append('search', debouncedSearch);
      const response = await axios.get(`${API}/admin/users?${params.toString()}`);
      const data = response.data;
      setUsers(data.users || data);
      if (data.pagination) setUserPagination(data.pagination);
    } catch (error) { toast.error('Erreur chargement utilisateurs'); }
    finally { setLoading(false); }
  };

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', orderPage);
      params.append('per_page', 50);
      if (debouncedSearch.trim()) params.append('search', debouncedSearch);
      const response = await axios.get(`${API}/orders?${params.toString()}`);
      const data = response.data;
      setOrders(data.orders || data);
      if (data.pagination) setOrderPagination(data.pagination);
    } catch (error) { toast.error('Erreur chargement commandes'); }
    finally { setLoading(false); }
  };

  const fetchProducts = async () => {
    try {
      const include = productFilter === 'all' || productFilter === 'archived';
      const response = await axios.get(`${API}/products${include ? '?include_archived=true' : ''}`);
      let data = response.data;
      if (productFilter === 'archived') data = data.filter(p => p.is_archived);
      else if (productFilter === 'active') data = data.filter(p => !p.is_archived);
      setProducts(data);
    } catch (error) { toast.error('Erreur chargement produits'); }
    finally { setLoading(false); }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setDiscountProducts(response.data);
    } catch (error) { /* silent */ }
  };

  const fetchCsvExports = async () => {
    try {
      const response = await axios.get(`${API}/export/csv/odoo/history`);
      setCsvExports(response.data);
    } catch (error) { /* silent */ }
    finally { setLoading(false); }
  };

  const fetchAdminStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats-detailed`);
      setAdminStats(response.data);
    } catch (error) { toast.error('Erreur chargement statistiques'); }
    finally { setLoading(false); }
  };

  const handleExportCsv = async (year, month) => {
    try {
      const response = await axios.get(`${API}/export/csv/odoo?year=${year}&month=${month}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `odoo_factures_${year}_${String(month).padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exporté');
    } catch (error) { toast.error('Erreur export CSV'); }
  };

  const handleDownloadStoredCsv = (exp) => {
    const blob = new Blob([exp.content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', exp.filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  // ---- User actions ----
  const openEditDialog = (u) => {
    setSelectedUser(u);
    // Parse existing address into parts
    const addr = u.address || '';
    const parts = addr.split(',').map(s => s.trim());
    let num = '', street = '', zip = '', city = '';
    if (parts.length >= 3) {
      const firstPart = parts[0];
      const numMatch = firstPart.match(/^(\d+\s*(?:bis|ter)?)\s+(.+)$/i);
      if (numMatch) { num = numMatch[1]; street = numMatch[2]; } else { street = firstPart; }
      zip = parts[parts.length - 2] || '';
      city = parts[parts.length - 1] || '';
    } else if (parts.length === 2) {
      street = parts[0]; city = parts[1];
    } else { street = addr; }
    setEditForm({ name: u.name, email: u.email, company_name: u.company_name || '', company_id: u.company_id || '', establishment_ids: u.establishment_ids || [], address_number: num, address_street: street, address_zip: zip, address_city: city, phone: u.phone || '', role: u.role, can_view_prices: u.can_view_prices || false, can_view_commercial_terms: u.can_view_commercial_terms || false });
    setEditDialogOpen(true);
  };

  const openDiscountDialog = (u) => {
    setSelectedUser(u);
    const currentDiscounts = u.discounts || {};
    setDiscountForm({ ...currentDiscounts });
    setDiscountDialogOpen(true);
  };

  const openClientDetail = async (u) => {
    setClientDetailUser(u);
    setClientDetailOpen(true);
    // Count orders for this client
    try {
      const res = await axios.get(`${API}/orders?client_id=${u.id}`);
      setClientOrderCount(Array.isArray(res.data) ? res.data.length : 0);
    } catch { setClientOrderCount(0); }
    // Get establishments this user belongs to
    const userEsts = (u.establishment_ids || []).map(eid => allEstablishments.find(e => e.id === eid)).filter(Boolean);
    setClientEstablishments(userEsts);
    // Get all establishments in the user's company
    if (u.company_id) {
      const compEsts = allEstablishments.filter(e => e.company_id === u.company_id);
      setCompanyEstablishments(compEsts);
    } else {
      setCompanyEstablishments([]);
    }
  };

  const handleToggleEstablishment = async (estId) => {
    if (!clientDetailUser) return;
    const current = clientDetailUser.establishment_ids || [];
    const updated = current.includes(estId) ? current.filter(id => id !== estId) : [...current, estId];
    try {
      await axios.put(`${API}/admin/users/${clientDetailUser.id}`, { establishment_ids: updated });
      toast.success('Établissements mis à jour');
      setClientDetailUser({ ...clientDetailUser, establishment_ids: updated });
      setClientEstablishments(updated.map(eid => allEstablishments.find(e => e.id === eid)).filter(Boolean));
      fetchUsers();
    } catch { toast.error('Erreur mise à jour établissements'); }
  };

  // Refresh companyEstablishments after establishment create/update/delete
  const refreshCompanyEstablishments = async (companyId) => {
    if (!companyId) return;
    await fetchEstablishments();
    try {
      const res = await axios.get(`${API}/establishments?company_id=${companyId}`);
      setCompanyEstablishments(res.data || []);
    } catch {}
  };

  // Créer un nouvel établissement (appelé depuis AddUserDialog OU ClientDetailDialog).
  // Retourne l'établissement créé (avec son id) pour permettre l'auto-rattachement.
  const handleCreateEstablishment = async (data) => {
    try {
      const res = await axios.post(`${API}/establishments`, data);
      const created = res.data;
      await fetchEstablishments();
      if (data.company_id) await refreshCompanyEstablishments(data.company_id);
      return created;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création établissement');
      return null;
    }
  };

  const handleUpdateEstablishment = async (data) => {
    if (!estEditTarget?.id) return;
    try {
      const res = await axios.put(`${API}/establishments/${estEditTarget.id}`, data);
      toast.success('Établissement mis à jour');
      setEstEditOpen(false);
      setEstEditTarget(null);
      if (res.data?.company_id) await refreshCompanyEstablishments(res.data.company_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur mise à jour');
    }
  };

  const handleDeleteEstablishment = async (est) => {
    if (!est?.id) return;
    if (!window.confirm(`Supprimer l'établissement « ${est.name} » ? Les utilisateurs rattachés seront détachés.`)) return;
    try {
      await axios.delete(`${API}/establishments/${est.id}`);
      toast.success('Établissement supprimé');
      await refreshCompanyEstablishments(est.company_id);
      // Retire l'id de l'utilisateur en cours si présent
      if (clientDetailUser && (clientDetailUser.establishment_ids || []).includes(est.id)) {
        const updated = (clientDetailUser.establishment_ids || []).filter(id => id !== est.id);
        setClientDetailUser({ ...clientDetailUser, establishment_ids: updated });
      }
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('commande(s)')) {
        if (window.confirm(`${detail}\n\nForcer la suppression malgré tout ?`)) {
          try {
            await axios.delete(`${API}/establishments/${est.id}?force=true`);
            toast.success('Établissement supprimé (avec commandes conservées)');
            await refreshCompanyEstablishments(est.company_id);
            fetchUsers();
          } catch { toast.error('Erreur suppression forcée'); }
        }
      } else {
        toast.error(detail || 'Erreur suppression');
      }
    }
  };

  const handleUpdateUser = async () => {
    try {
      const payload = {
        ...editForm,
        address: [editForm.address_number, editForm.address_street, editForm.address_zip, editForm.address_city].filter(Boolean).join(', ')
      };
      delete payload.address_number;
      delete payload.address_street;
      delete payload.address_zip;
      delete payload.address_city;
      await axios.put(`${API}/admin/users/${selectedUser.id}`, payload);
      toast.success('Utilisateur mis à jour');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) { toast.error('Erreur mise à jour'); }
  };

  const handleUpdateDiscounts = async () => {
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/discounts`, discountForm);
      toast.success('Conditions commerciales mises à jour');
      setDiscountDialogOpen(false);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur mise à jour remises'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) { toast.error('Erreur suppression'); }
  };

  const handleAddUser = async () => {
    if (!addUserForm.name || !addUserForm.email || !addUserForm.password) {
      toast.error('Nom, email et mot de passe sont obligatoires');
      return;
    }
    if (addUserForm.role === 'client') {
      const hasCompany = !!(addUserForm.company_id || (addUserForm.company_name || '').trim());
      if (!hasCompany) {
        toast.error('Un client doit être rattaché à une société.');
        return;
      }
      if (addUserForm.company_id && (addUserForm.establishment_ids || []).length === 0) {
        toast.error('Un client doit être rattaché à au moins un établissement.');
        return;
      }
    }
    try {
      const payload = {
        ...addUserForm,
        address: [addUserForm.address_number, addUserForm.address_street, addUserForm.address_zip, addUserForm.address_city].filter(Boolean).join(', ')
      };
      delete payload.address_number;
      delete payload.address_street;
      delete payload.address_zip;
      delete payload.address_city;
      await axios.post(`${API}/admin/users`, payload);
      toast.success('Utilisateur créé');
      setAddUserDialogOpen(false);
      setAddUserForm({ name: '', email: '', password: '', role: 'client', company_name: '', company_id: '', address_number: '', address_street: '', address_zip: '', address_city: '', phone: '', can_view_prices: false, can_view_commercial_terms: false, establishment_ids: [] });
      fetchUsers();
      fetchCompanies();
      fetchEstablishments();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur création'); }
  };

  // ---- Product actions ----
  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({ name: product.name, description: product.description || '', category: product.category || 'embout', base_price: product.base_price || 0 });
    } else {
      setEditingProduct(null);
      setProductForm({ name: '', description: '', category: 'embout', base_price: 0 });
    }
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name) { toast.error('Le nom est obligatoire'); return; }
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productForm);
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API}/products`, productForm);
        toast.success('Produit créé');
      }
      setProductDialogOpen(false);
      fetchProducts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur produit'); }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;
    try {
      const res = await axios.delete(`${API}/products/${productToDelete.id}`);
      const msg = res.data.action === 'archived'
        ? `Produit archivé (utilisé dans ${res.data.used_count} commande(s))`
        : 'Produit supprimé définitivement';
      toast.success(msg);
      setDeleteProductDialogOpen(false);
      setProductToDelete(null);
      fetchProducts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur suppression'); }
  };

  const formatDate = (d) => { if (!d) return '—'; const date = new Date(d); return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('fr-FR'); };

  // Urgence dynamique (72h normal / 120h pour embout intégral)
  const getUrgencyData = (order) => {
    const result = getOrderBusinessHours(order);
    if (!result) return { style: {}, className: '', hours: 0, level: 'none', hasStl: false, threshold: 72 };
    const { hours, isFinished, hasStl, threshold } = result;
    const level = getUrgencyLevel(hours, isFinished, threshold);
    const styleMap = {
      ok: { '--urgency-bg': '#dcfce7', '--urgency-color': '#14532d' },
      warning: { '--urgency-bg': '#fef9c3', '--urgency-color': '#713f12' },
      critical: { '--urgency-bg': '#ffedd5', '--urgency-color': '#7c2d12' },
      overdue: { '--urgency-bg': '#fee2e2', '--urgency-color': '#7f1d1d' },
      finished: {},
      none: {}
    };
    return {
      style: styleMap[level] || {},
      className: level !== 'finished' && level !== 'none' ? 'urgency-row' : '',
      hours,
      level,
      hasStl,
      threshold,
    };
  };
  
  const handleSendInvite = async (userId, userName, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/invite`);
      if (res.data.warning) {
        toast.warning(res.data.warning);
      } else {
        toast.success(`Invitation envoyée à ${userName}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };
  const handleSendPasswordReset = async (u, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Envoyer un email de réinitialisation de mot de passe à ${u.name} (${u.email}) ?`)) return;
    try {
      const res = await axios.post(`${API}/admin/users/${u.id}/send-password-reset`);
      toast.success(res.data.message || `Email de réinitialisation envoyé à ${u.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };
  const getRoleBadge = (role) => {
    const styles = { admin: 'bg-zinc-900 text-white', technician: 'bg-amber-500 text-white', client: 'bg-zinc-200 text-zinc-700' };
    const labels = { admin: 'Admin', technician: 'Technicien', client: 'Client' };
    return <span className={`px-2 py-1 text-xs uppercase tracking-wider rounded-sm ${styles[role]}`}>{labels[role]}</span>;
  };

  const stats = useMemo(() => ({
    totalUsers: users.length,
    clients: users.filter(u => u.role === 'client').length,
    totalOrders: orders.length,
    pendingOrders: orders.filter(o => o.status === 'pending').length
  }), [users, orders]);

  const addFormEstablishments = useMemo(() =>
    allEstablishments.filter(e => e.company_id === addUserForm.company_id),
    [allEstablishments, addUserForm.company_id]
  );

  const editFormEstablishments = useMemo(() =>
    allEstablishments.filter(e => e.company_id === editForm.company_id),
    [allEstablishments, editForm.company_id]
  );

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="admin-panel">
      <header className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Ear size={24} weight="duotone" className="sm:w-7 sm:h-7" />
            <span className="font-heading font-bold text-base sm:text-lg tracking-tight">HAMAC</span>
            <span className="text-zinc-500 text-xs sm:text-sm ml-1 sm:ml-2 hidden sm:inline">Administration</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/dashboard"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white"><Package size={18} /><span className="hidden sm:inline ml-2">Dashboard</span></Button></Link>
            <span className="text-xs sm:text-sm text-zinc-400 hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400 hover:text-white" data-testid="logout-btn"><SignOut size={18} /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border border-zinc-200 rounded-sm flex flex-wrap gap-1">
            <TabsTrigger value="dashboard" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-dashboard"><ChartLine size={16} className="sm:mr-2" /><span className="hidden sm:inline">Vue d'ensemble</span></TabsTrigger>
            <TabsTrigger value="users" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-users"><Users size={16} className="sm:mr-2" /><span className="hidden sm:inline">Utilisateurs</span></TabsTrigger>
            <TabsTrigger value="products" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-products"><Tag size={16} className="sm:mr-2" /><span className="hidden sm:inline">Produits</span></TabsTrigger>
            <TabsTrigger value="orders" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-orders"><Package size={16} className="sm:mr-2" /><span className="hidden sm:inline">Commandes</span></TabsTrigger>
            <TabsTrigger value="exports" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-exports"><FileCsv size={16} className="sm:mr-2" /><span className="hidden sm:inline">Export Odoo</span></TabsTrigger>
            <TabsTrigger value="shopify" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-shopify"><ShoppingCart size={16} className="sm:mr-2" /><span className="hidden sm:inline">Shopify</span></TabsTrigger>
            <TabsTrigger value="client-accounts" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-client-accounts"><Buildings size={16} className="sm:mr-2" /><span className="hidden sm:inline">Comptes</span></TabsTrigger>
            <TabsTrigger value="sav" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-sav"><Wrench size={16} className="sm:mr-2" /><span className="hidden sm:inline">SAV</span></TabsTrigger>
            <TabsTrigger value="sav-stats" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-sav-stats"><ChartLine size={16} className="sm:mr-2" /><span className="hidden sm:inline">Stats SAV</span></TabsTrigger>
            <TabsTrigger value="mdr" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-mdr"><ShieldCheck size={16} className="sm:mr-2" /><span className="hidden sm:inline">Qualité / MDR</span></TabsTrigger>
            <TabsTrigger value="tech-value" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-tech-value"><ChartLine size={16} className="sm:mr-2" /><span className="hidden sm:inline">Valeur</span></TabsTrigger>
          </TabsList>

          {/* Dashboard Tab — Vue d'ensemble avec statistiques */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
              <div className="bg-white p-4 sm:p-6 border border-zinc-200"><Users size={20} className="mb-2 sm:mb-3 text-zinc-600 sm:w-6 sm:h-6" /><p className="label-overline text-[10px] sm:text-xs">Total Utilisateurs</p><p className="font-heading font-bold text-xl sm:text-2xl">{stats.totalUsers}</p></div>
              <div className="bg-white p-4 sm:p-6 border border-zinc-200"><Users size={20} className="mb-2 sm:mb-3 text-zinc-600 sm:w-6 sm:h-6" /><p className="label-overline text-[10px] sm:text-xs">Clients</p><p className="font-heading font-bold text-xl sm:text-2xl">{stats.clients}</p></div>
              <div className="bg-white p-4 sm:p-6 border border-zinc-200"><Package size={20} className="mb-2 sm:mb-3 text-zinc-600 sm:w-6 sm:h-6" /><p className="label-overline text-[10px] sm:text-xs">Total Commandes</p><p className="font-heading font-bold text-xl sm:text-2xl">{stats.totalOrders}</p></div>
              <div className="bg-white p-4 sm:p-6 border border-zinc-200"><Package size={20} className="mb-2 sm:mb-3 text-rose-600 sm:w-6 sm:h-6" /><p className="label-overline text-[10px] sm:text-xs">En Attente</p><p className="font-heading font-bold text-xl sm:text-2xl">{stats.pendingOrders}</p></div>
            </div>
            <AdminStats stats={adminStats} />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-3">
              <div className="relative flex-1">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <Input
                  placeholder="Rechercher par nom, email, entreprise ou n° compte (multi-mots : 'jean hamac')"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10 rounded-sm bg-white"
                  data-testid="user-search" />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                    title="Effacer la recherche"
                    data-testid="user-search-clear">
                    <X size={16} />
                  </button>
                )}
              </div>
              <Button onClick={() => setAddUserDialogOpen(true)} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 w-full sm:w-auto" data-testid="add-user-btn">
                <Plus size={18} className="mr-2" />Ajouter
              </Button>
            </div>
            {/* Result indicator */}
            <div className="mb-4 flex items-center gap-2 text-xs text-zinc-500" data-testid="user-search-meta">
              {debouncedSearch ? (
                <>
                  <span><strong className="text-zinc-700">{userPagination.total}</strong> resultat{userPagination.total > 1 ? 's' : ''} pour « <strong>{debouncedSearch}</strong> »</span>
                  {searchQuery !== debouncedSearch && <span className="text-amber-600">Recherche en cours...</span>}
                </>
              ) : (
                <span><strong className="text-zinc-700">{userPagination.total}</strong> utilisateur{userPagination.total > 1 ? 's' : ''} au total</span>
              )}
            </div>
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              {loading ? <div className="p-12 text-center"><div className="spinner mx-auto mb-4" />Chargement...</div> : (
                <Table className="dense-table">
                  <TableHeader><TableRow className="bg-zinc-50"><TableHead className="label-overline">Nom</TableHead><TableHead className="label-overline">Email</TableHead><TableHead className="label-overline hidden md:table-cell">Société</TableHead><TableHead className="label-overline hidden lg:table-cell">Établissement(s)</TableHead><TableHead className="label-overline hidden sm:table-cell">Rôle</TableHead><TableHead className="label-overline hidden lg:table-cell">Droits</TableHead><TableHead className="label-overline hidden lg:table-cell">RGPD</TableHead><TableHead className="label-overline hidden md:table-cell">Remises</TableHead><TableHead className="label-overline text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {users.map((u) => {
                      const discountCount = u.discounts ? Object.keys(u.discounts).filter(k => u.discounts[k] > 0).length : 0;
                      return (
                        <TableRow key={u.id} className="table-row-hover cursor-pointer" data-testid={`user-row-${u.id}`} onClick={() => u.role === 'client' ? openClientDetail(u) : openEditDialog(u)}>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{u.email}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div className="text-sm">{u.company_name || '-'}</div>
                            {(() => { const comp = companies.find(c => c.id === u.company_id); return comp?.account_number ? <span className="text-[10px] text-zinc-400">{comp.account_number}</span> : null; })()}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {(u.establishment_ids || []).length > 0 ? (
                              <div className="flex flex-col gap-0.5">
                                {(u.establishment_ids || []).map(eid => {
                                  const est = allEstablishments.find(e => e.id === eid);
                                  return est ? <span key={eid} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700">{est.name}</span> : null;
                                })}
                              </div>
                            ) : <span className="text-xs text-zinc-300">—</span>}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">{getRoleBadge(u.role)}</TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {u.role === 'client' ? (
                              <div className="flex flex-col gap-0.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${u.can_view_prices ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-400'}`}>
                                  Tarifs {u.can_view_prices ? '✓' : '✗'}
                                </span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${u.can_view_commercial_terms ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-400'}`}>
                                  Remises {u.can_view_commercial_terms ? '✓' : '✗'}
                                </span>
                              </div>
                            ) : <span className="text-xs text-zinc-300">—</span>}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {u.privacy_accepted ? (
                              <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-sm" title={`v${u.privacy_version || '?'} - ${u.privacy_accepted_at ? new Date(u.privacy_accepted_at).toLocaleDateString('fr-FR') : '?'}`}>
                                Accepté {u.privacy_accepted_at ? new Date(u.privacy_accepted_at).toLocaleDateString('fr-FR') : ''}
                              </span>
                            ) : (
                              <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-sm">En attente</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {u.role === 'client' ? (
                              <div className="flex flex-col gap-0.5">
                                {discountCount > 0
                                  ? <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-sm">{discountCount} produit(s)</span>
                                  : <span className="text-xs text-zinc-400">Aucune</span>
                                }
                                {u.promo_status && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium ${
                                    u.promo_status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                                    u.promo_status === 'invited' ? 'bg-amber-50 text-amber-700' :
                                    u.promo_status === 'converted' ? 'bg-blue-50 text-blue-700' :
                                    'bg-zinc-100 text-zinc-500'
                                  }`} data-testid={`promo-badge-${u.id}`}>
                                    {u.promo_status === 'active' ? `Promo ${u.promo_quota_used || 0}/${u.promo_quota_total || 10}` :
                                     u.promo_status === 'invited' ? 'Promo invité' :
                                     u.promo_status === 'converted' ? 'Converti' : 'Promo expirée'}
                                  </span>
                                )}
                              </div>
                            ) : <span className="text-xs text-zinc-300">—</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" onClick={(e) => handleSendInvite(u.id, u.name, e)} title="Envoyer invitation compte" data-testid={`invite-${u.id}`}><EnvelopeSimple size={16} /></Button>
                              <Button variant="ghost" size="sm" onClick={(e) => handleSendPasswordReset(u, e)} title="Réinitialiser le mot de passe (email envoyé à l'utilisateur)" data-testid={`pwd-reset-${u.id}`} className="text-amber-600 hover:text-amber-700"><Key size={16} /></Button>
                              {u.role === 'client' && !u.promo_status && <Button variant="ghost" size="sm" onClick={(e) => openPromoInvite(u, e)} title="Offre promotionnelle" data-testid={`promo-${u.id}`} className="text-emerald-600 hover:text-emerald-700"><Gift size={16} /></Button>}
                              {u.role === 'client' && <Button variant="ghost" size="sm" onClick={(e) => handlePromoBonus(u, e)} title="Offrir 5 embouts (cumulable, cliquable à tout moment)" data-testid={`promo-bonus-${u.id}`} className="text-yellow-500 hover:text-yellow-600"><Gift size={16} weight="fill" /></Button>}
                              {u.role === 'client' && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDefaultMarquageDialog(u); }} title={u.default_marquage_image_id ? 'Marquage par défaut actif (cliquer pour modifier/retirer)' : 'Configurer un marquage par défaut (logo, non facturé)'} data-testid={`default-marquage-${u.id}`} className={u.default_marquage_image_id ? 'text-indigo-600 hover:text-indigo-700' : ''}><Stamp size={16} weight={u.default_marquage_image_id ? 'fill' : 'regular'} /></Button>}
                              {u.role === 'client' && <Button variant="ghost" size="sm" onClick={() => openDiscountDialog(u)} title="Conditions commerciales" data-testid={`discount-${u.id}`}><Percent size={16} /></Button>}
                              <Button variant="ghost" size="sm" onClick={() => openEditDialog(u)} data-testid={`edit-${u.id}`}><Pencil size={16} /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="text-rose-600 hover:text-rose-700" data-testid={`delete-${u.id}`}><Trash size={16} /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex gap-4 mb-6 items-center">
              <div className="flex-1">
                <h2 className="text-lg font-heading font-semibold">Gestion des produits</h2>
                <p className="text-sm text-zinc-500">Ajouter, modifier ou supprimer les produits du catalogue</p>
              </div>
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger className="w-[180px] rounded-sm bg-white" data-testid="product-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Produits actifs</SelectItem>
                  <SelectItem value="archived">Produits archivés</SelectItem>
                  <SelectItem value="all">Tous les produits</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => openProductDialog()} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 w-full sm:w-auto" data-testid="add-product-btn">
                <Plus size={18} className="mr-2" />Ajouter
              </Button>
            </div>
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              {loading ? <div className="p-12 text-center"><div className="spinner mx-auto mb-4" />Chargement...</div> : products.length === 0 ? (
                <div className="p-12 text-center text-zinc-500"><Tag size={48} className="mx-auto mb-4 text-zinc-300" /><p>{productFilter === 'archived' ? 'Aucun produit archivé' : 'Aucun produit'}</p></div>
              ) : (
                <Table className="dense-table">
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="label-overline">Nom</TableHead>
                      <TableHead className="label-overline hidden sm:table-cell">Description</TableHead>
                      <TableHead className="label-overline hidden sm:table-cell">Catégorie</TableHead>
                      <TableHead className="label-overline">Prix</TableHead>
                      <TableHead className="label-overline hidden sm:table-cell">Statut</TableHead>
                      <TableHead className="label-overline text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((p) => (
                      <TableRow key={p.id} className={`table-row-hover ${p.is_archived ? 'opacity-50' : ''}`} data-testid={`product-row-${p.id}`}>
                        <TableCell className="font-medium text-sm">{p.name}</TableCell>
                        <TableCell className="text-sm text-zinc-600 max-w-xs truncate hidden sm:table-cell">{p.description}</TableCell>
                        <TableCell className="hidden sm:table-cell"><span className="px-2 py-1 bg-zinc-100 text-xs uppercase rounded-sm">{p.category}</span></TableCell>
                        <TableCell className="font-mono text-sm">{p.base_price?.toFixed(2)} &euro;</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {p.is_archived
                            ? <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-sm" data-testid={`product-archived-${p.id}`}>Archivé</span>
                            : <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-sm">Actif</span>
                          }
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            {!p.is_archived && <Button variant="ghost" size="sm" onClick={() => openProductDialog(p)} data-testid={`edit-product-${p.id}`}><Pencil size={16} /></Button>}
                            {!p.is_archived && <Button variant="ghost" size="sm" onClick={() => { setProductToDelete(p); setDeleteProductDialogOpen(true); }} className="text-rose-600 hover:text-rose-700" data-testid={`delete-product-${p.id}`}><Trash size={16} /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
              <div className="relative flex-1">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <Input placeholder="Rechercher par n° série, référence..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-sm bg-white" data-testid="order-search" />
              </div>
            </div>
            <div className="bg-white border border-zinc-200">
              {loading ? <div className="p-12 text-center"><div className="spinner mx-auto mb-4" />Chargement...</div> : orders.length === 0 ? <div className="p-12 text-center text-zinc-500"><Package size={48} className="mx-auto mb-4 text-zinc-300" /><p>Aucune commande</p></div> : (
                <Table className="dense-table">
                  <TableHeader><TableRow className="bg-zinc-50"><TableHead className="label-overline">Saisie</TableHead><TableHead className="label-overline">Livraison</TableHead><TableHead className="label-overline">72h</TableHead><TableHead className="label-overline">Client</TableHead><TableHead className="label-overline">Réf. Patient</TableHead><TableHead className="label-overline">Prix</TableHead><TableHead className="label-overline">Statut</TableHead><TableHead className="label-overline text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {orders.map((o) => {
                      const urgency = getUrgencyData(o);
                      return (
                      <TableRow key={o.id} style={urgency.style} className={`${urgency.className} table-row-hover cursor-pointer`} onClick={() => { setPriceOrder(o); setPriceDialogOpen(true); }}>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(o.created_at)}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(o.delivery_date)}</TableCell>
                        <TableCell className="whitespace-nowrap" data-testid={`admin-counter-${o.id}`}>
                          <AdminCounterBadge hours={urgency.hours} level={urgency.level} hasStl={urgency.hasStl} threshold={urgency.threshold} />
                        </TableCell>
                        <TableCell><p className="font-medium">{o.client_name}</p><p className="text-xs text-zinc-500">{o.client_company}</p></TableCell>
                        <TableCell className="font-medium">{o.patient_reference}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {o.final_price != null ? (
                            <div>
                              <span>{o.final_price.toFixed(2)} €</span>
                              {o.discount_pct > 0 && <span className="text-xs text-emerald-600 ml-1">(-{o.discount_pct}%)</span>}
                            </div>
                          ) : '—'}
                        </TableCell>
                        <TableCell><span className={o.status === 'pending' ? 'badge-pending' : o.status === 'design' ? 'badge-design' : o.status === 'in_progress' ? 'badge-in-progress' : o.status === 'cancelled' ? 'badge-cancelled' : 'badge-completed'}>{o.status === 'pending' ? 'En attente' : o.status === 'design' ? 'Design' : o.status === 'in_progress' ? 'Fabrication' : o.status === 'cancelled' ? 'Annulée' : 'Terminée'}</span></TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            {o.stl_files && o.stl_files.length > 0 && (
                              <Button variant="ghost" size="sm" className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 h-7 w-7 p-0"
                                onClick={async () => {
                                  for (const f of o.stl_files) {
                                    try {
                                      const r = await axios.get(`${API}/files/${f.file_id}/download`, { responseType: 'blob' });
                                      const url = window.URL.createObjectURL(r.data);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = r.headers['x-filename'] || f.custom_filename || f.filename || 'fichier.stl';
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                    } catch { toast.error(`Erreur téléchargement ${f.filename}`); }
                                  }
                                }}
                                data-testid={`admin-download-stl-${o.id}`} title={`Télécharger STL (${o.stl_files.length})`}
                              ><DownloadSimple size={15} /></Button>
                            )}
                            <span className="text-xs text-zinc-400 hover:text-zinc-700 underline cursor-pointer" onClick={() => { setPriceOrder(o); setPriceDialogOpen(true); }} data-testid={`price-detail-${o.id}`}>Détail</span>
                            <Button variant="ghost" size="sm" className="text-rose-500 hover:text-rose-700 h-7 w-7 p-0" onClick={() => { setOrderToDelete(o); setDeleteDialogOpen(true); }} data-testid={`delete-order-${o.id}`} title="Supprimer"><Trash size={15} /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Exports Odoo Tab */}
          <TabsContent value="exports">
            <div className="space-y-6">
              <div className="flex gap-4 items-start">
                <div className="flex-1">
                  <h2 className="text-lg font-heading font-semibold">Exports</h2>
                  <p className="text-sm text-zinc-500">Exports CSV pour Odoo et récapitulatifs PDF des commandes par client.</p>
                </div>
                <Button
                  onClick={() => setRecapOpen(true)}
                  variant="outline"
                  className="rounded-sm border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white"
                  data-testid="open-monthly-recap-btn"
                >
                  <DownloadSimple size={18} className="mr-2" />
                  Export récapitulatif PDF
                </Button>
              </div>

              {/* Export manuel */}
              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-heading font-semibold mb-4 pb-2 border-b border-zinc-200">Export manuel</h3>
                <div className="flex gap-4 items-end">
                  <div>
                    <Label className="label-overline mb-1 block">Mois</Label>
                    <select className="h-10 px-3 border border-zinc-200 rounded-sm bg-white text-sm" id="export-month" defaultValue={new Date().getMonth() + 1}>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'][m-1]}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="label-overline mb-1 block">Année</Label>
                    <select className="h-10 px-3 border border-zinc-200 rounded-sm bg-white text-sm" id="export-year" defaultValue={new Date().getFullYear()}>
                      {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <Button onClick={() => { const m = parseInt(document.getElementById('export-month').value); const y = parseInt(document.getElementById('export-year').value); handleExportCsv(y, m); }} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="export-csv-btn">
                    <DownloadSimple size={18} className="mr-2" />Télécharger le CSV
                  </Button>
                </div>
              </div>

              {/* Structure CSV */}
              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-heading font-semibold mb-4 pb-2 border-b border-zinc-200">Structure du CSV</h3>
                <div className="overflow-x-auto">
                  <table className="text-xs border-collapse w-full">
                    <thead><tr className="bg-zinc-50">
                      {['Partner','Invoice Date','Journal','Currency','Product','Quantity','Unit Price','Description','Numero de serie','Oreille','Reference patient','Type embout','Statut fabrication'].map(col => <th key={col} className="px-3 py-2 border border-zinc-200 text-left font-semibold">{col}</th>)}
                    </tr></thead>
                    <tbody><tr>
                      <td className="px-3 py-2 border border-zinc-200">Labo Test</td><td className="px-3 py-2 border border-zinc-200">2026-03-22</td><td className="px-3 py-2 border border-zinc-200">Ventes</td><td className="px-3 py-2 border border-zinc-200">EUR</td><td className="px-3 py-2 border border-zinc-200">Canule Silicone</td><td className="px-3 py-2 border border-zinc-200">1</td><td className="px-3 py-2 border border-zinc-200">39.50</td><td className="px-3 py-2 border border-zinc-200">Canule Silicone - REF001</td><td className="px-3 py-2 border border-zinc-200 font-mono">26REF1234L</td><td className="px-3 py-2 border border-zinc-200">L</td><td className="px-3 py-2 border border-zinc-200">REF001</td><td className="px-3 py-2 border border-zinc-200">canule</td><td className="px-3 py-2 border border-zinc-200">Terminée</td>
                    </tr></tbody>
                  </table>
                </div>
                <div className="mt-4 bg-amber-50 border border-amber-200 p-4 rounded-sm text-sm">
                  <p className="font-semibold text-amber-800 mb-2">Ajouter de nouvelles colonnes pour Odoo :</p>
                  <ol className="list-decimal ml-4 text-amber-700 space-y-1">
                    <li>Dans Odoo : <strong>Configuration &gt; Technique &gt; Champs</strong> — Créer un nouveau champ sur le modèle <code>account.move.line</code></li>
                    <li>Nommer le champ avec le préfixe <code>x_studio_</code> (ex: <code>x_studio_numero_serie</code>)</li>
                    <li>Dans le backend (<code>server.py</code>), ajouter la colonne dans <code>generate_odoo_csv()</code> : header + valeur dans la boucle</li>
                    <li>Odoo ne traitera <strong>que</strong> les colonnes qu'il connaît ou que vous avez configurées</li>
                  </ol>
                </div>
              </div>

              {/* Export SAV */}
              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-heading font-semibold mb-4 pb-2 border-b border-zinc-200">Export SAV pour Odoo</h3>
                <p className="text-sm text-zinc-500 mb-4">Exporter les demandes SAV avec décision au format CSV pour import Odoo.</p>
                <div className="flex gap-4 items-end">
                  <div>
                    <Label className="label-overline mb-1 block">Mois</Label>
                    <select className="h-10 px-3 border border-zinc-200 rounded-sm bg-white text-sm" id="export-sav-month" defaultValue={new Date().getMonth() + 1}>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'][m-1]}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="label-overline mb-1 block">Année</Label>
                    <select className="h-10 px-3 border border-zinc-200 rounded-sm bg-white text-sm" id="export-sav-year" defaultValue={new Date().getFullYear()}>
                      {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <Button onClick={() => {
                    const m = parseInt(document.getElementById('export-sav-month').value);
                    const y = parseInt(document.getElementById('export-sav-year').value);
                    const url = `${API}/export/csv/odoo/sav?year=${y}&month=${m}`;
                    window.open(url, '_blank');
                  }} variant="outline" className="rounded-sm" data-testid="export-sav-csv-btn">
                    <DownloadSimple size={18} className="mr-2" />Télécharger CSV SAV
                  </Button>
                </div>
              </div>

              {/* Historique exports */}
              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-heading font-semibold mb-4 pb-2 border-b border-zinc-200">Historique des exports automatiques (CRON)</h3>
                {csvExports.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-4">Aucun export automatique pour le moment. Le prochain sera généré le dernier jour du mois à 22h00.</p>
                ) : (
                  <div className="space-y-2">
                    {csvExports.map((exp) => (
                      <div key={exp.id} className="flex items-center justify-between p-3 border border-zinc-200 rounded-sm hover:bg-zinc-50">
                        <div>
                          <p className="font-medium text-sm">{exp.filename}</p>
                          <p className="text-xs text-zinc-500">{exp.rows} lignes — Généré le {new Date(exp.generated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => handleDownloadStoredCsv(exp)} className="rounded-sm"><DownloadSimple size={16} className="mr-2" />Télécharger</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="shopify">
            <ShopifyTab />
          </TabsContent>

          <TabsContent value="client-accounts">
            <ClientAccountManager onCompanyMarquage={openCompanyMarquageDialog} />
          </TabsContent>

          <TabsContent value="sav">
            <AdminSavModule />
          </TabsContent>

          <TabsContent value="mdr">
            <MdrSettingsPanel />
          </TabsContent>

          <TabsContent value="sav-stats">
            <SavStats />
          </TabsContent>

          <TabsContent value="tech-value">
            <TechValueStats />
          </TabsContent>
        </Tabs>
      </div>

      <AddUserDialog
        open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}
        form={addUserForm} setForm={setAddUserForm}
        companies={companies} establishments={addFormEstablishments}
        onSubmit={handleAddUser}
        onCreateEstablishment={handleCreateEstablishment}
      />

      <MonthlyRecapDialog open={recapOpen} onOpenChange={setRecapOpen} />

      <EditUserDialog
        open={editDialogOpen} onOpenChange={setEditDialogOpen}
        form={editForm} setForm={setEditForm}
        companies={companies} establishments={editFormEstablishments}
        onSubmit={handleUpdateUser}
      />

      <ClientDetailDialog
        open={clientDetailOpen} onOpenChange={setClientDetailOpen}
        user={clientDetailUser} orderCount={clientOrderCount}
        companyEstablishments={companyEstablishments}
        onToggleEstablishment={handleToggleEstablishment}
        onEdit={() => { setClientDetailOpen(false); openEditDialog(clientDetailUser); }}
        onDiscount={() => { setClientDetailOpen(false); openDiscountDialog(clientDetailUser); }}
        onPromoInvite={(u) => { setClientDetailOpen(false); openPromoInvite(u); }}
        onCreateEstablishment={(u) => { setEstEditTarget({ company_id: u.company_id }); setEstEditOpen(true); }}
        onEditEstablishment={(est) => { setEstEditTarget(est); setEstEditOpen(true); }}
        onDeleteEstablishment={handleDeleteEstablishment}
        onCompanyMarquage={(u) => {
          const comp = companies.find(c => c.id === u.company_id);
          if (comp) { setClientDetailOpen(false); openCompanyMarquageDialog(comp); }
          else { toast.error('Société introuvable'); }
        }}
      />

      <EstablishmentEditDialog
        open={estEditOpen} onOpenChange={setEstEditOpen}
        establishment={estEditTarget}
        onSubmit={async (data) => {
          if (estEditTarget?.id) {
            await handleUpdateEstablishment(data);
          } else {
            const companyId = estEditTarget?.company_id || clientDetailUser?.company_id;
            if (!companyId) { toast.error('Société non déterminée'); return; }
            const created = await handleCreateEstablishment({ ...data, company_id: companyId });
            if (created) { setEstEditOpen(false); setEstEditTarget(null); toast.success('Établissement créé'); }
          }
        }}
      />

      <DiscountDialog
        open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}
        user={selectedUser} products={discountProducts}
        form={discountForm} setForm={setDiscountForm}
        onSubmit={handleUpdateDiscounts}
      />

      <ProductDialog
        open={productDialogOpen} onOpenChange={setProductDialogOpen}
        editing={editingProduct} form={productForm} setForm={setProductForm}
        onSubmit={handleSaveProduct}
      />

      <PriceBreakdownDialog
        open={priceDialogOpen} onOpenChange={setPriceDialogOpen}
        order={priceOrder}
      />

      <DeleteOrderDialog
        open={deleteDialogOpen}
        onOpenChange={(v) => { setDeleteDialogOpen(v); if (!v) setOrderToDelete(null); }}
        order={orderToDelete} onConfirm={handleDeleteOrder}
      />

      <DeleteProductDialog
        open={deleteProductDialogOpen}
        onOpenChange={(v) => { setDeleteProductDialogOpen(v); if (!v) setProductToDelete(null); }}
        product={productToDelete} onConfirm={handleDeleteProduct}
      />

      <PromoInviteDialog
        open={promoDialogOpen} onOpenChange={setPromoDialogOpen}
        user={promoUser} onConfirm={handlePromoInvite}
        loading={promoLoading}
      />

      <DefaultMarquageDialog
        open={defaultMarquageDialogOpen}
        onOpenChange={(v) => { setDefaultMarquageDialogOpen(v); if (!v) { setDefaultMarquageUser(null); setDefaultMarquagePreview(''); } }}
        user={defaultMarquageUser}
        preview={defaultMarquagePreview}
        uploading={defaultMarquageUploading}
        onUpload={handleDefaultMarquageUpload}
        onDelete={handleDefaultMarquageDelete}
        target={defaultMarquageTarget}
      />
    </div>
  );
}

function DefaultMarquageDialog({ open, onOpenChange, user, target, preview, uploading, onUpload, onDelete }) {
  // Rétrocompat : si target absent (ancien appel avec user), on déduit un target 'user'.
  const t = target || (user ? { type: 'user', name: user.name } : null);
  // Coefficient d'échelle logo/serial (1.0×, 1.25×, 1.5×, 1.75×, 2.0×, 2.5×).
  // Priorité de lecture : target.current_scale > 1.5 (défaut).
  const [scale, setScale] = React.useState(() => (t?.current_scale ?? 1.5));
  const [savingScale, setSavingScale] = React.useState(false);
  // Nonce servant à cache-buster le PNG de prévisualisation à chaque changement.
  const [previewNonce, setPreviewNonce] = React.useState(0);
  React.useEffect(() => { setScale(t?.current_scale ?? 1.5); setPreviewNonce((n) => n + 1); }, [t?.id, t?.current_scale]);
  if (!t) return null;
  const isCompany = t.type === 'company';

  const handleScaleSave = async (newScale) => {
    setScale(newScale);
    setPreviewNonce((n) => n + 1); // rafraîchit le PNG de preview immédiatement
    setSavingScale(true);
    try {
      const url = isCompany
        ? `${API}/admin/companies/${t.id}/marquage-scale`
        : `${API}/admin/users/${t.id}/marquage-scale`;
      await axios.put(url, { marquage_logo_scale: newScale });
      toast.success(`Coefficient logo enregistré : ${newScale.toFixed(2)}×`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur enregistrement coefficient');
    } finally {
      setSavingScale(false);
    }
  };

  // URL du PNG de prévisualisation. Regénéré côté serveur avec le `scale` fourni
  // (pas encore obligatoirement enregistré) — l'admin peut donc évaluer visuellement
  // avant de valider un preset. `previewNonce` sert de cache-buster.
  const previewUrl = t.id
    ? `${API}/admin/marquage-preview?target_type=${t.type}&target_id=${t.id}&scale=${scale}&side=left&_=${previewNonce}`
    : null;
  return (
    <div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => onOpenChange(false)} data-testid="default-marquage-dialog">
          <div className="bg-white rounded-sm border border-zinc-200 w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-heading text-lg font-semibold">
                  {isCompany ? 'Marquage société' : 'Marquage par défaut'}
                </h2>
                <p className="text-sm text-zinc-600 mt-1">
                  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm mr-2 ${isCompany ? 'bg-indigo-100 text-indigo-800' : 'bg-zinc-100 text-zinc-800'}`}>
                    {isCompany ? 'Société' : 'Utilisateur'}
                  </span>
                  <strong>{t.name}</strong>
                  {!isCompany && user?.email && <> · {user.email}</>}
                </p>
              </div>
              <button onClick={() => onOpenChange(false)} className="text-zinc-500 hover:text-zinc-900" data-testid="default-marquage-close">✕</button>
            </div>

            <p className="text-xs text-zinc-600 mb-4">
              {isCompany
                ? <>Ce logo est <strong>hérité par tous les utilisateurs</strong> de la société qui n'ont pas configuré leur propre logo. Priorité : marquage ponctuel de commande &gt; logo utilisateur &gt; <strong>logo société</strong> &gt; texte HAMAC.</>
                : <>Ce logo remplacera automatiquement « HAMAC » sur les étiquettes série de <strong>toutes les commandes</strong> de ce client, sans frais supplémentaires. Il <strong>prévaut</strong> sur le logo société s'il y en a un.</>
              }
            </p>

            {preview ? (
              <div className="p-4 bg-zinc-100 border border-zinc-200 rounded-sm mb-4 text-center" data-testid="default-marquage-preview">
                <p className="text-xs text-zinc-600 mb-2">Logo actuel :</p>
                <img src={preview} alt="Marquage par défaut" className="max-h-32 mx-auto bg-white p-3 rounded" />
              </div>
            ) : (
              <div className="p-4 bg-zinc-50 border border-dashed border-zinc-300 rounded-sm mb-4 text-center text-sm text-zinc-500" data-testid="default-marquage-empty">
                {isCompany
                  ? 'Aucun logo société configuré. Les utilisateurs verront « HAMAC » (sauf logo utilisateur personnel).'
                  : 'Aucun logo utilisateur. Le logo société sera utilisé s\'il existe, sinon « HAMAC ».'}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="label-overline mb-2 block">PNG uniquement, 5 Mo max</label>
                <input
                  type="file"
                  accept="image/png,.png"
                  onChange={onUpload}
                  disabled={uploading}
                  className="block w-full text-sm text-zinc-700 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-zinc-900 file:text-white file:cursor-pointer hover:file:bg-zinc-800 disabled:opacity-50"
                  data-testid="default-marquage-upload-input"
                />
                {uploading && <p className="text-xs text-zinc-500 mt-2">Upload en cours...</p>}
              </div>

              {preview && (
                <Button variant="ghost" onClick={onDelete} className="text-rose-600 hover:text-rose-700 w-full" data-testid="default-marquage-delete-btn">
                  Retirer le marquage par défaut
                </Button>
              )}
            </div>

            {/* Coefficient d'échelle logo/serial — configurable par target */}
            <div className="mt-5 pt-4 border-t border-zinc-200" data-testid="marquage-scale-block">
              <label className="label-overline mb-2 block">
                Taille du logo vs numéro de série
              </label>
              <p className="text-xs text-zinc-500 mb-3">
                Coefficient de largeur : à <strong>1.0×</strong> le logo fait la même largeur que le S/N, à <strong>1.5×</strong> il est 50 % plus large, etc.
                {!isCompany && ' Si non défini, la valeur de la société est utilisée.'}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="marquage-scale-options">
                {[1.0, 1.25, 1.5, 1.75, 2.0, 2.5].map((v) => (
                  <button
                    key={v}
                    type="button"
                    disabled={savingScale}
                    onClick={() => handleScaleSave(v)}
                    className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
                      Math.abs(scale - v) < 1e-6
                        ? 'bg-zinc-900 text-white border-zinc-900'
                        : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500'
                    } disabled:opacity-50`}
                    data-testid={`marquage-scale-${v.toFixed(2).replace('.', '_')}`}
                  >
                    {v.toFixed(2)}×
                  </button>
                ))}
              </div>
              {savingScale && <p className="text-xs text-zinc-500 mt-2">Enregistrement…</p>}

              {/* Prévisualisation live du PNG serial généré côté serveur */}
              <div className="mt-4 p-3 bg-zinc-900 rounded-sm" data-testid="marquage-scale-preview">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Aperçu ({scale.toFixed(2)}× — côté gauche)</p>
                <div className="bg-white rounded-sm p-2 flex items-center justify-center min-h-[100px]">
                  {previewUrl ? (
                    <AuthImage
                      url={previewUrl}
                      alt="Aperçu du marquage"
                      className="max-w-full max-h-32 object-contain"
                    />
                  ) : (
                    <span className="text-xs text-zinc-400">Sélectionnez une cible pour afficher l&apos;aperçu.</span>
                  )}
                </div>
                <p className="text-[10px] text-zinc-400 mt-2 leading-tight">
                  Numéro de série fictif <code className="bg-zinc-800 px-1 py-0.5 rounded">HAMDEMOL</code> — l&apos;aperçu se met à jour à chaque changement de coefficient.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

